import React, { useEffect, useRef } from 'react';
import { View, Text, StyleSheet, Animated } from 'react-native';
import Icon from 'react-native-vector-icons/MaterialIcons';

export interface TravelLoadingStateProps {
  /**
   * 飞机图标动画值（可选，如果不提供会自动创建）
   */
  flyAnimation?: Animated.Value;
  
  /**
   * 旋转动画值（可选，如果不提供会自动创建）
   */
  rotateAnimation?: Animated.Value;
  
  /**
   * 主题色
   */
  themeColor: string;
  
  /**
   * 加载文本
   */
  loadingText: string;
  
  /**
   * 加载副文本
   */
  loadingSubtext?: string;
}

/**
 * Travel 模块加载状态组件
 * 
 * 显示飞机图标的飞行动画和加载文本
 * 
 * @example
 * ```tsx
 * // 简单使用（自动创建动画）
 * <TravelLoadingState
 *   themeColor="#FF6B6B"
 *   loadingText="正在加载旅行项目..."
 *   loadingSubtext="请稍候"
 * />
 * 
 * // 自定义动画
 * const flyAnimation = useRef(new Animated.Value(0)).current;
 * const rotateAnimation = useRef(new Animated.Value(0)).current;
 * 
 * <TravelLoadingState
 *   flyAnimation={flyAnimation}
 *   rotateAnimation={rotateAnimation}
 *   themeColor="#FF6B6B"
 *   loadingText="正在加载旅行项目..."
 *   loadingSubtext="请稍候"
 * />
 * ```
 */
export const TravelLoadingState: React.FC<TravelLoadingStateProps> = ({
  flyAnimation: externalFlyAnimation,
  rotateAnimation: externalRotateAnimation,
  themeColor,
  loadingText,
  loadingSubtext,
}) => {
  // 内部动画值（当外部没有提供时使用）
  const internalFlyAnimation = useRef(new Animated.Value(0)).current;
  const internalRotateAnimation = useRef(new Animated.Value(0)).current;
  
  // 使用外部提供的动画值，或者使用内部创建的
  const flyAnimation = externalFlyAnimation || internalFlyAnimation;
  const rotateAnimation = externalRotateAnimation || internalRotateAnimation;
  
  // 启动动画（仅当使用内部动画时）
  useEffect(() => {
    if (!externalFlyAnimation || !externalRotateAnimation) {
      // 飞行动画
      const flyLoop = Animated.loop(
        Animated.sequence([
          Animated.timing(flyAnimation, {
            toValue: -10,
            duration: 1500,
            useNativeDriver: true,
          }),
          Animated.timing(flyAnimation, {
            toValue: 10,
            duration: 1500,
            useNativeDriver: true,
          }),
        ])
      );
      
      // 旋转动画
      const rotateLoop = Animated.loop(
        Animated.sequence([
          Animated.timing(rotateAnimation, {
            toValue: 1,
            duration: 2000,
            useNativeDriver: true,
          }),
          Animated.timing(rotateAnimation, {
            toValue: 0,
            duration: 2000,
            useNativeDriver: true,
          }),
        ])
      );
      
      flyLoop.start();
      rotateLoop.start();
      
      return () => {
        flyLoop.stop();
        rotateLoop.stop();
      };
    }
  }, [flyAnimation, rotateAnimation, externalFlyAnimation, externalRotateAnimation]);
  return (
    <View style={styles.container}>
      <Animated.View
        style={[
          styles.iconContainer,
          {
            transform: [
              { translateY: flyAnimation },
              {
                rotate: rotateAnimation.interpolate({
                  inputRange: [0, 1],
                  outputRange: ['-5deg', '5deg'],
                }),
              },
            ],
          },
        ]}
      >
        <Icon name="flight" size={48} color={themeColor} />
      </Animated.View>
      <Text style={styles.loadingText}>{loadingText}</Text>
      {loadingSubtext && (
        <Text style={styles.loadingSubtext}>{loadingSubtext}</Text>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  iconContainer: {
    marginBottom: 20,
  },
  loadingText: {
    fontSize: 16,
    color: '#666',
    marginTop: 16,
    marginBottom: 8,
    fontWeight: '600',
  },
  loadingSubtext: {
    fontSize: 14,
    color: '#999',
    textAlign: 'center',
  },
});
